<template>
  <div class="upload-image-wrapper" :style="wrapperStyle">
    <el-upload
      class="avatar-uploader"
      :action="uploadUrl"
      :show-file-list="false"
      :on-success="handleSuccess"
      :before-upload="beforeUpload"
      :data="uploadData"
      :headers="uploadHeaders"
      :disabled="disabled"
      :with-credentials="true"
    >
      <img v-if="imageUrl" :src="imageUrl" class="avatar" :style="imgStyle">
      <div v-else class="upload-placeholder" :style="placeholderStyle">
        <img v-if="placeholderImg" :src="placeholderImg" alt="上传图片">
        <i v-else class="el-icon-plus avatar-uploader-icon" :style="placeholderIconStyle"></i>
      </div>
    </el-upload>
    <div v-if="imageUrl && !disabled" class="delete-btn-wrapper" :style="deleteBtnWrapperStyle">
      <el-button class="delete-btn" size="mini" type="danger" circle icon="el-icon-delete" @click.stop="handleDelete"></el-button>
    </div>
    <div v-if="tip" class="upload-tip" :style="tipStyle">{{ tip }}</div>
  </div>
</template>

<script>
/**
 * 引入 Vuex store 实例，用于获取全局状态
 */
import store from '@/store'
/**
 * 引入文件相关 API 方法：获取文件信息、删除文件
 */
import { getFileInfo, deleteFile } from '@/api/api.js'

/**
 * 图片上传组件
 * @description 基于 Element UI 的 el-upload 封装的图片上传组件，支持图片预览、删除、尺寸限制、类型限制等功能
 * @module UploadImage
 */
export default {
  /** 组件名称 */
  name: 'UploadImage',
  /** 组件属性定义 */
  props: {
    /**
     * 绑定值，支持 v-model 双向绑定
     * 可以是文件编号(file_no)或完整的图片URL(http开头)
     * @type {string}
     * @default ''
     */
    value: {
      type: String, // 值类型为字符串
      default: '' // 默认值为空字符串
    },
    /**
     * 上传文件关联的表名
     * @type {string}
     * @default 'bxws_member_info'
     */
    tableName: {
      type: String, // 值类型为字符串
      default: 'bxws_member_info' // 默认表名为会员信息表
    },
    /**
     * 上传文件关联的字段名
     * @type {string}
     * @default ''
     */
    columns: {
      type: String, // 值类型为字符串
      default: '' // 默认值为空字符串
    },
    /**
     * 缩略图类型，用于生成不同规格的缩略图
     * 命名规则：{模式}_{尺寸}
     * 常用可选值（需后端支持，仅供参考）：
     * - fwsu_100: 固定宽度100px，等比缩放
     * - fwsu_200: 固定宽度200px，等比缩放
     * - fwsu_400: 固定宽度400px，等比缩放
     * - fhsu_100: 固定高度100px，等比缩放
     * - fwhc_200x200: 固定宽高200x200，居中裁剪
     * 命名说明：fw=FixedWidth(固定宽度), fh=FixedHeight(固定高度), su=ScaleUniformly(等比缩放), hc=HeightCrop(裁剪)
     * @type {string}
     * @default 'fwsu_100'
     */
    thumbnailType: {
      type: String, // 值类型为字符串
      default: 'fwsu_100' // 默认缩略图类型为等宽缩放100px
    },
    /**
     * 允许上传的文件大小上限，单位MB
     * @type {number}
     * @default 2
     */
    fileSize: {
      type: Number, // 值类型为数字
      default: 2 // 默认大小限制为2MB
    },
    /**
     * 允许上传的文件类型数组
     * @type {Array<string>}
     * @default ['jpg', 'jpeg', 'png']
     */
    fileTypes: {
      type: Array, // 值类型为数组
      default: () => ['jpg', 'jpeg', 'png'] // 默认允许jpg、jpeg、png格式
    },
    /**
     * 是否禁用上传功能
     * @type {boolean}
     * @default false
     */
    disabled: {
      type: Boolean, // 值类型为布尔值
      default: false // 默认不禁用
    },
    /**
     * 上传提示文字
     * @type {string}
     * @default ''
     */
    tip: {
      type: String, // 值类型为字符串
      default: '' // 默认无提示文字
    },
    /**
     * 占位图片地址，未上传时显示的图片
     * @type {string}
     * @default ''
     */
    placeholderImg: {
      type: String, // 值类型为字符串
      default: '' // 默认无占位图片
    },
    /**
     * 上传组件宽度，支持数字(px)或字符串(如'100%')
     * @type {string|number}
     * @default 178
     */
    width: {
      type: [String, Number], // 值类型为字符串或数字
      default: 178 // 默认宽度为178px
    },
    /**
     * 上传组件高度，支持数字(px)或字符串(如'100%')
     * @type {string|number}
     * @default 178
     */
    height: {
      type: [String, Number], // 值类型为字符串或数字
      default: 178 // 默认高度为178px
    },
    /**
     * 删除时是否调用API接口删除服务器上的文件
     * @type {boolean}
     * @default false
     */
    deleteWithApi: {
      type: Boolean, // 值类型为布尔值
      default: false // 默认不调用API删除
    }
  },
  /**
   * 组件响应式数据定义
   * @returns {Object} 组件数据对象
   */
  data() {
    return {
      /**
       * 文件信息对象，存储上传文件的详细信息（file_no、fileurl、src_name等）
       * @type {Object|null}
       */
      fileInfo: null, // 文件信息，初始为null
      /**
       * 标记上传是否成功，用于避免value监听时的重复加载
       * @type {boolean}
       * @private
       */
      _isUploadSuccess: false // 上传成功标记，初始为false
    }
  },
  /** 计算属性集合 */
  computed: {
    /**
     * 上传接口的完整URL地址
     * @type {string}
     * @returns {string} 上传接口地址
     */
    uploadUrl() {
      // 拼接store中的hostPath和上传路径，返回完整的上传接口URL
      return `${store.getters.hostPath}/file/upload`
    },
    /**
     * 上传请求的请求头配置，包含认证token
     * @type {Object}
     * @returns {Object} 请求头对象
     */
    uploadHeaders() {
      // 获取当前认证token
      const token = this.getToken
      // 返回包含两种格式认证头的对象（兼容不同后端命名规范）
      return {
        'bx_auth_ticket': token, // 下划线格式的认证头
        'bx-auth-ticket': token // 中划线格式的认证头
      }
    },
    /**
     * 获取当前用户认证token
     * @type {string}
     * @returns {string} 认证token字符串
     */
    getToken() {
      // 优先从store获取，其次从sessionStorage获取，都没有则返回空字符串
      return store.getters.getToken || sessionStorage.getItem('bx_auth_ticket') || ''
    },
    /**
     * 上传请求携带的表单数据
     * @type {Object}
     * @returns {Object} 上传数据对象
     */
    uploadData() {
      // 构建上传所需的基础数据对象
      const data = {
        serviceName: 'srv_bxfile_service', // 文件服务名称
        interfaceName: 'add', // 接口名称：新增
        app_no: 'ws', // 应用编号
        table_name: this.tableName, // 关联表名
        thumbnailType: this.thumbnailType, // 缩略图类型
        columns: this.columns // 关联字段名
      }
      // 如果已有值且不是http开头的URL，则添加file_no字段用于更新已有文件
      if (this.value && this.value.indexOf('http') !== 0) {
        data.file_no = this.value // 设置文件编号
      }
      // 返回构建好的上传数据
      return data
    },
    /**
     * 图片的完整访问URL
     * @type {string}
     * @returns {string} 图片访问地址
     */
    imageUrl() {
      // 如果没有值，直接返回空字符串
      if (!this.value) return ''
      // 如果值是http开头的完整URL，直接返回
      if (this.value.indexOf('http') === 0) return this.value
      // 如果有文件信息且包含fileurl字段
      if (this.fileInfo && this.fileInfo.fileurl) {
        // 如果fileurl是完整的http URL，直接返回
        if (this.fileInfo.fileurl.indexOf('http') === 0) {
          return this.fileInfo.fileurl
        }
        // 如果fileurl是相对路径，拼接成完整的下载地址
        if (this.fileInfo.fileurl) {
          // 拼接hostPath和下载接口地址，带上filePath参数
          let url = `${store.getters.hostPath}/file/download?filePath=${this.fileInfo.fileurl}`
          // 获取认证token
          const token = this.getToken
          // 如果有token，追加到URL参数中
          if (token) {
            url = `${url}&bx_auth_ticket=${token}`
          }
          // 打印构造的图片URL，用于调试
          console.log('imageUrl constructed:', url, 'fileurl:', this.fileInfo.fileurl)
          // 返回完整的图片URL
          return url
        }
      }
      // 没有文件信息或fileurl时，打印调试日志
      console.log('imageUrl: no fileInfo or fileurl', this.value, this.fileInfo)
      // 返回空字符串
      return ''
    },
    /**
     * 图片的样式对象（宽高）
     * @type {Object}
     * @returns {Object} 图片样式对象
     */
    imgStyle() {
      // 返回图片的宽高样式，数字类型自动拼接px单位
      return {
        width: typeof this.width === 'number' ? `${this.width}px` : this.width, // 宽度样式
        height: typeof this.height === 'number' ? `${this.height}px` : this.height // 高度样式
      }
    },
    /**
     * 占位区域的样式对象（宽高）
     * @type {Object}
     * @returns {Object} 占位区域样式对象
     */
    placeholderStyle() {
      // 返回占位区域的宽高样式，数字类型自动拼接px单位
      return {
        width: typeof this.width === 'number' ? `${this.width}px` : this.width, // 宽度样式
        height: typeof this.height === 'number' ? `${this.height}px` : this.height // 高度样式
      }
    },
    /**
     * 占位图标（加号）的样式对象
     * @type {Object}
     * @returns {Object} 占位图标样式对象
     */
    placeholderIconStyle() {
      // 计算宽度数值，数字直接使用，字符串则解析为整数
      const w = typeof this.width === 'number' ? this.width : parseInt(this.width)
      // 计算高度数值，数字直接使用，字符串则解析为整数
      const h = typeof this.height === 'number' ? this.height : parseInt(this.height)
      // 返回占位图标的样式对象，宽高和行高保持一致以实现垂直居中
      return {
        width: `${w}px`, // 图标宽度
        height: `${h}px`, // 图标高度
        lineHeight: `${h}px` // 行高，用于垂直居中文本/图标
      }
    },
    /**
     * 外层容器的样式对象（宽高）
     * @type {Object}
     * @returns {Object} 外层容器样式对象
     */
    wrapperStyle() {
      // 返回外层容器的宽高样式，数字类型自动拼接px单位
      return {
        width: typeof this.width === 'number' ? `${this.width}px` : this.width, // 宽度样式
        height: typeof this.height === 'number' ? `${this.height}px` : this.height // 高度样式
      }
    },
    /**
     * 删除按钮容器的样式对象（宽高）
     * @type {Object}
     * @returns {Object} 删除按钮容器样式对象
     */
    deleteBtnWrapperStyle() {
      // 返回删除按钮容器的宽高样式，数字类型自动拼接px单位
      return {
        width: typeof this.width === 'number' ? `${this.width}px` : this.width, // 宽度样式
        height: typeof this.height === 'number' ? `${this.height}px` : this.height // 高度样式
      }
    },
    /**
     * 提示文字的样式对象（宽度）
     * @type {Object}
     * @returns {Object} 提示文字样式对象
     */
    tipStyle() {
      // 返回提示文字的宽度样式，数字类型自动拼接px单位
      return {
        width: typeof this.width === 'number' ? `${this.width}px` : this.width // 宽度样式
      }
    }
  },
  /** 监听器集合 */
  watch: {
    /**
     * 监听 value 属性变化，加载对应的文件信息
     * @type {Object}
     * @property {boolean} immediate - 是否立即执行监听
     * @property {Function} handler - 监听处理函数
     */
    value: {
      immediate: true, // 组件创建时立即执行一次
      /**
       * value变化的处理函数
       * @param {string} newVal - 新的value值
       */
      handler(newVal) {
        // 如果有新值且不是http开头的URL（说明是文件编号）
        if (newVal && newVal.indexOf('http') !== 0) {
          // 如果是刚上传成功的，重置标记并返回，避免重复加载
          if (this._isUploadSuccess) {
            this._isUploadSuccess = false // 重置上传成功标记
            return // 直接返回，不加载文件信息
          }
          // 如果已有文件信息且文件编号相同，无需重复加载
          if (this.fileInfo && this.fileInfo.file_no === newVal) {
            return // 直接返回
          }
          // 调用API加载文件信息
          this.loadFileInfo(newVal)
        } else if (!newVal) {
          // 如果值被清空，重置文件信息和上传成功标记
          this.fileInfo = null // 清空文件信息
          this._isUploadSuccess = false // 重置上传成功标记
        }
      }
    }
  },
  /** 方法集合 */
  methods: {
    /**
     * 根据文件编号加载文件详细信息
     * @async
     * @param {string} fileNo - 文件编号
     * @returns {Promise<void>} 无返回值
     */
    async loadFileInfo(fileNo) {
      try {
        // 调用API获取文件信息
        const res = await getFileInfo(fileNo)
        // 兼容不同层级的响应数据结构，提取实际数据
        const data = res && res.data && res.data.body && res.data.body.data ? res.data.body.data : (res && res.data ? res.data : [])
        // 如果数据是数组且有数据，取第一条作为文件信息
        if (Array.isArray(data) && data.length > 0) {
          this.fileInfo = data[0] // 保存第一条文件信息
        }
      } catch (err) {
        // 捕获并打印错误信息
        console.error('加载文件信息失败:', err)
      }
    },
    /**
     * 上传前的文件校验钩子函数
     * @param {File} file - 待上传的文件对象
     * @returns {boolean} 是否允许上传，true允许，false禁止
     */
    beforeUpload(file) {
      // 从文件名中提取扩展名并转为小写
      const type = file.name.slice(file.name.lastIndexOf('.') + 1).toLowerCase()
      // 检查文件类型是否在允许的类型列表中
      const isTypeValid = this.fileTypes.includes(type)
      // 如果文件类型不合法，提示错误并返回false
      if (!isTypeValid) {
        this.$message.error(`只能上传${this.fileTypes.join('/')}格式的图片!`) // 显示错误提示
        return false // 禁止上传
      }
      // 检查文件大小是否在限制范围内（转换为MB单位）
      const isSizeValid = file.size / 1024 / 1024 < this.fileSize
      // 如果文件大小超过限制，提示错误并返回false
      if (!isSizeValid) {
        this.$message.error(`图片大小不能超过 ${this.fileSize}MB!`) // 显示错误提示
        return false // 禁止上传
      }
      // 所有校验都通过，返回true允许上传
      return true
    },
    /**
     * 上传成功回调处理函数
     * @param {Object} response - 上传接口响应数据
     * @param {File} file - 上传的文件对象
     */
    handleSuccess(response, file) {
      // 打印原始响应数据，用于调试
      console.log('upload response:', response)
      // 标准化响应数据格式，统一字段名
      const normalizedResponse = this.normalizeResponse(response)
      // 打印标准化后的响应数据，用于调试
      console.log('normalized response:', normalizedResponse)
      // 判断上传是否成功：三种成功状态判断方式
      const isSuccess = normalizedResponse.state === 'SUCCESS' || normalizedResponse.resultCode === 'SUCCESS' || normalizedResponse.state === undefined
      
      // 如果上传成功
      if (isSuccess) {
        this._isUploadSuccess = true // 标记上传成功
        this.fileInfo = normalizedResponse // 保存标准化后的文件信息
        // 初始化modelValue，优先使用file_no
        let modelValue = normalizedResponse.file_no || ''
        // 如果有fileurl且是完整的http URL，则使用fileurl作为值
        if (normalizedResponse.fileurl && normalizedResponse.fileurl.indexOf('http') === 0) {
          modelValue = normalizedResponse.fileurl // 使用完整URL作为值
        }
        // 打印上传成功信息，用于调试
        console.log('upload success, modelValue:', modelValue, 'fileInfo:', this.fileInfo)
        // 触发input事件，更新v-model绑定值
        this.$emit('input', modelValue)
        // 触发change事件，通知父组件数据变化
        this.$emit('change', normalizedResponse)
        // 显示上传成功提示
        this.$message.success('上传成功')
      } else {
        // 上传失败，显示错误提示信息
        this.$message.error(normalizedResponse.resultMessage || normalizedResponse.message || '上传失败')
      }
    },
    /**
     * 标准化接口响应数据，统一字段命名
     * @param {Object} response - 原始响应数据
     * @returns {Object} 标准化后的数据对象，包含file_no、fileurl、src_name等字段
     */
    normalizeResponse(response) {
      // 如果响应为空，返回空对象
      if (!response) return {}
      // 初始化data为响应对象本身
      let data = response
      // 尝试从response.body.data中提取数据
      if (response.body && response.body.data) {
        data = response.body.data // 使用body.data中的数据
      } else if (response.response && response.response.body && response.response.body.data) {
        // 尝试从response.response.body.data中提取数据（嵌套响应结构）
        data = response.response.body.data // 使用嵌套的body.data中的数据
      }
      // 返回标准化后的数据对象，展开原始数据并统一关键字段名
      return {
        ...data, // 展开原始数据，保留所有原有字段
        file_no: data.file_no || data.id || data.fileId || '', // 统一文件编号字段名
        fileurl: data.fileurl || data.file_url || data.path || data.url || data.filePath || '', // 统一文件URL字段名
        src_name: data.src_name || data.fileName || data.name || '' // 统一文件名字段名
      }
    },
    /**
     * 删除图片处理函数
     * @returns {void} 无返回值
     */
    handleDelete() {
      // 如果没有值，直接返回
      if (!this.value) return
      // 如果不使用API删除（直接清除）
      if (this.deleteWithApi === false) {
        this.$emit('input', '') // 触发input事件，清空绑定值
        this.$emit('change', null) // 触发change事件，通知数据变化
        this.fileInfo = null // 清空文件信息
        this.$message.success('删除成功') // 显示成功提示
        return // 直接返回
      }
      // 如果值是http开头的完整URL（外链图片），直接清除
      if (this.value.indexOf('http') === 0) {
        this.$emit('input', '') // 触发input事件，清空绑定值
        this.$emit('change', null) // 触发change事件，通知数据变化
        this.fileInfo = null // 清空文件信息
        this.$message.success('删除成功') // 显示成功提示
        return // 直接返回
      }
      // 获取文件的URL路径（使用可选链避免报错）
      const fileurl = this.fileInfo?.fileurl
      // 如果获取不到文件URL，提示错误
      if (!fileurl) {
        this.$message.error('获取文件信息失败') // 显示错误提示
        return // 直接返回
      }
      // 调用删除文件API
      deleteFile({ fileurl }).then(res => {
        // 判断删除是否成功
        if (res.resultCode === 'SUCCESS' || res.state === 'SUCCESS') {
          this.$emit('input', '') // 触发input事件，清空绑定值
          this.$emit('change', null) // 触发change事件，通知数据变化
          this.fileInfo = null // 清空文件信息
          this.$message.success('删除成功') // 显示成功提示
        } else {
          // 删除失败，显示错误信息
          this.$message.error(res.resultMessage || '删除失败')
        }
      }).catch(err => {
        // 捕获删除请求的异常，打印错误日志
        console.error('删除文件失败:', err)
        // 显示删除失败提示
        this.$message.error('删除失败')
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.upload-image-wrapper {
  display: inline-block;
  position: relative;
}

.upload-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  overflow: hidden;
  width: 100%;
  height: 100%;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  &:hover {
    border-color: #409EFF;
  }
}

.avatar-uploader {
  width: 100%;
  height: 100%;
}

.avatar-uploader .el-upload {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100%;
}

.avatar-uploader .el-upload:hover {
  border-color: #409EFF;
}

.avatar-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  text-align: center;
}

.avatar {
  display: block;
  object-fit: cover;
  width: 100%;
  height: 100%;
}

.delete-btn-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
  opacity: 0;
  transition: opacity 0.3s;
  border-radius: 6px;
}

.upload-image-wrapper:hover .delete-btn-wrapper {
  opacity: 1;
}

.delete-btn {
  background-color: #F56C6C;
  border-color: #F56C6C;
}

.upload-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>